import { openDB } from "idb";
import api, { API } from "../../lib/api";

// Durable on-device POS database (IndexedDB). Survives refresh, tab close, sleep and offline periods.
const dbp = openDB("hz-pos", 1, {
    upgrade(db) {
        db.createObjectStore("meta");
        db.createObjectStore("items", { keyPath: "item_id" });
        const t = db.createObjectStore("txns", { keyPath: "id" });
        t.createIndex("event_id", "event_id");
        db.createObjectStore("customers", { keyPath: "wa" });
        db.createObjectStore("images");
    },
});

export const getMeta = async (k) => (await dbp).get("meta", k);
export const setMeta = async (k, v) => (await dbp).put("meta", v, k);

export function normWa(v) {
    let d = String(v || "").replace(/\D/g, "");
    if (d.startsWith("0")) d = "62" + d.slice(1);
    else if (d.startsWith("8")) d = "62" + d;
    return d;
}

export async function initDevice() {
    if (navigator.storage?.persist) navigator.storage.persist().catch(() => {});
    if (!(await getMeta("device"))) {
        await setMeta("device", Math.random().toString(36).slice(2, 5).toUpperCase());
    }
}

export async function loadLocal() {
    const db = await dbp;
    const [event, items, customers, txns, prepared] = await Promise.all([
        db.get("meta", "event"), db.getAll("items"), db.getAll("customers"), db.getAll("txns"), db.get("meta", "prepared"),
    ]);
    return { event: event || null, items, customers, txns, prepared: !!event && prepared === `v2:${event.id}` };
}

// Downloads the locked Event Snapshot. Local remaining stock always wins for the same event.
export async function refreshFromServer() {
    const { data } = await api.get("/pos/active", { timeout: 12000 });
    const db = await dbp;
    const tx = db.transaction(["meta", "items", "customers"], "readwrite");
    const meta = tx.objectStore("meta");
    const items = tx.objectStore("items");
    const local = await meta.get("event");
    if (data.event) {
        if (!local || local.id !== data.event.id) {
            await items.clear();
            for (const it of data.items) await items.put(it);
            await meta.delete("prepared");
            await meta.delete("cart");
        } else {
            for (const it of data.items) {
                const cur = await items.get(it.item_id);
                await items.put(cur ? { ...it, remaining_qty: cur.remaining_qty } : it);
            }
        }
        await meta.put(data.event, "event");
    } else if (local && local.status !== "CLOSED") {
        await meta.put({ ...local, status: "CLOSED" }, "event");
    }
    await putServerCustomers(tx.objectStore("customers"), data.customers);
    await tx.done;
}

async function putServerCustomers(store, list) {
    for (const c of list || []) {
        const cur = await store.get(c.wa);
        await store.put({ ...(cur || {}), wa: c.wa, name: cur?.name || c.name, server_total_purchases: c.total_purchases || 0,
            server_total_spent: c.total_spent || 0, server_last_purchase_at: c.last_purchase_at || null });
    }
}

export async function refreshCustomers() {
    const { data } = await api.get("/pos/customers", { timeout: 12000 });
    const db = await dbp;
    const tx = db.transaction("customers", "readwrite");
    await putServerCustomers(tx.store, data);
    await tx.done;
}

const imgKey = (eid, iid) => `v2:${eid}:${iid}`;

// Caches lightweight thumbnails on the device so the cashier grid never needs the network.
export async function cacheImages(event, items) {
    const db = await dbp;
    const token = localStorage.getItem("hoshizora_token") || "";
    const todo = [];
    for (const it of items) {
        if (it.image?.type === "catalog" && it.image?.src && (await db.get("images", imgKey(event.id, it.item_id))) === undefined) todo.push(it);
    }
    let failed = 0;
    const worker = async () => {
        while (todo.length) {
            const it = todo.shift();
            try {
                const r = await fetch(`${API}/pos/thumb/${event.id}/${it.item_id}`, { headers: { Authorization: `Bearer ${token}` } });
                if (r.ok) await db.put("images", await r.blob(), imgKey(event.id, it.item_id));
                else if (r.status === 404) await db.put("images", null, imgKey(event.id, it.item_id));
                else failed += 1;
            } catch {
                failed += 1;
            }
        }
    };
    await Promise.all([worker(), worker(), worker(), worker()]);
    if (!failed) await setMeta("prepared", `v2:${event.id}`);
    return failed;
}

const urlCache = new Map();
export async function imageUrls(event, items) {
    if (!event) return {};
    const db = await dbp;
    const out = {};
    for (const it of items) {
        const k = imgKey(event.id, it.item_id);
        if (!urlCache.has(k)) {
            const blob = await db.get("images", k);
            if (blob) urlCache.set(k, URL.createObjectURL(blob));
        }
        if (urlCache.has(k)) out[it.item_id] = urlCache.get(k);
    }
    return out;
}

const yymmdd = (d) => d.toISOString().slice(2, 10).replace(/-/g, "");

// Local-first checkout: one atomic IndexedDB transaction saves the sale, reduces stock and records the customer.
export async function checkoutLocal(event, cart, { name, wa, method }) {
    const db = await dbp;
    const tx = db.transaction(["items", "txns", "customers", "meta"], "readwrite");
    tx.done.catch(() => {});
    const items = tx.objectStore("items");
    const meta = tx.objectStore("meta");
    const lines = [];
    for (const l of cart) {
        const it = await items.get(l.item_id);
        if (!it || it.remaining_qty < l.qty) {
            tx.abort();
            throw new Error(`Not enough stock for ${it?.name || l.item_id}`);
        }
        lines.push({ it, qty: l.qty });
    }
    for (const { it, qty } of lines) await items.put({ ...it, remaining_qty: it.remaining_qty - qty });
    const seq = ((await meta.get("seq")) || 0) + 1;
    await meta.put(seq, "seq");
    const now = new Date();
    const txn = {
        id: crypto.randomUUID(),
        number: `HZ-${yymmdd(now)}-${await meta.get("device")}-${String(seq).padStart(4, "0")}`,
        event_id: event.id, event_name: event.name, created_at: now.toISOString(),
        customer_name: name.trim(), customer_wa: wa, payment_method: method,
        items: lines.map(({ it, qty }) => ({ item_id: it.item_id, sku: it.sku, name: it.name, variant: it.variant, qty, price: it.price, line_total: qty * it.price })),
        sync_status: "SAVED", receipt: { channel: "whatsapp", status: "PENDING" },
    };
    txn.total = txn.items.reduce((s, x) => s + x.line_total, 0);
    await tx.objectStore("txns").put(txn);
    const cs = tx.objectStore("customers");
    const cur = await cs.get(wa);
    await cs.put({ server_total_purchases: 0, server_total_spent: 0, server_last_purchase_at: null, ...(cur || {}), wa, name: name.trim() });
    await meta.delete("cart");
    await tx.done;
    return txn;
}

// Idempotent server sync: the server keys on the local transaction id, so retries never duplicate a sale.
export async function syncPending() {
    const db = await dbp;
    const pending = (await db.getAll("txns")).filter((t) => t.sync_status === "SAVED" || t.sync_status === "PENDING");
    let synced = 0;
    for (let i = 0; i < pending.length; i += 20) {
        const batch = pending.slice(i, i + 20);
        let res;
        try {
            res = await api.post("/pos/sync", {
                transactions: batch.map((t) => ({ id: t.id, number: t.number, event_id: t.event_id, created_at: t.created_at,
                    customer_name: t.customer_name, customer_wa: t.customer_wa, payment_method: t.payment_method,
                    items: t.items.map((x) => ({ item_id: x.item_id, qty: x.qty, price: x.price })) })),
            }, { timeout: 20000 });
        } catch (e) {
            for (const t of batch) if (t.sync_status === "SAVED") await db.put("txns", { ...t, sync_status: "PENDING" });
            throw e;
        }
        for (const r of res.data.results) {
            const t = batch.find((x) => x.id === r.id);
            const ok = r.status === "SYNCED" || r.status === "ALREADY_SYNCED";
            if (ok) synced += 1;
            await db.put("txns", { ...t, sync_status: ok ? "SYNCED" : "REJECTED", sync_error: ok ? null : r.reason, synced_at: ok ? new Date().toISOString() : null });
        }
    }
    return { attempted: pending.length, synced };
}

const setReceipt = async (id, patch) => {
    const db = await dbp;
    const t = await db.get("txns", id);
    const next = { ...t, receipt: { ...t.receipt, ...patch } };
    await db.put("txns", next);
    return next;
};

// Sends (or queues) the WhatsApp receipt for an already-saved sale. Never touches the sale itself.
export async function sendReceipt(id) {
    const db = await dbp;
    let t = await setReceipt(id, { requested: true });
    if (t.receipt.status === "SENT") return { status: "SENT" };
    if (t.sync_status !== "SYNCED") {
        await syncPending().catch(() => {});
        t = await db.get("txns", id);
        if (t.sync_status !== "SYNCED") return { status: "PENDING", queued: true };
    }
    const prev = t.receipt.status;
    await setReceipt(id, { status: "SENDING" });
    try {
        const { data } = await api.post(`/pos/transactions/${id}/receipt/send`, {}, { timeout: 20000 });
        await setReceipt(id, { ...data.receipt, requested: false });
        return { status: data.receipt.status };
    } catch (e) {
        const detail = e.response?.data?.detail;
        if (!e.response) {
            await setReceipt(id, { status: prev === "FAILED" ? "FAILED" : "PENDING" });
            return { status: "PENDING", queued: true };
        }
        if (e.response.status === 503 || e.response.status === 409) {
            await setReceipt(id, { status: e.response.status === 409 && /already sent/i.test(detail || "") ? "SENT" : prev, last_error: detail });
            throw new Error(detail);
        }
        await setReceipt(id, { status: "FAILED", requested: false, last_error: detail || "Send failed" });
        throw new Error(detail || "WhatsApp send failed");
    }
}

// Auto-retry only receipts the cashier asked to send that never left PENDING (FAILED needs a manual resend).
export async function retryRequestedReceipts() {
    const db = await dbp;
    const due = (await db.getAll("txns")).filter((t) => t.receipt?.requested && t.receipt.status === "PENDING" && t.sync_status === "SYNCED");
    for (const t of due) await sendReceipt(t.id).catch(() => {});
}
